<template>
  <div class="container py-5">
    <h2 class="mb-4 fw-bold text-center text-primary">Exportaciones y Reportes PDF</h2>

    <!-- Filtros -->
    <div class="row g-4 mb-5">
      <!-- Proyecto con búsqueda -->
      <div class="col-md-6">
        <label class="form-label fw-semibold">Proyecto</label>
        <input
          type="text"
          class="form-control mb-2"
          placeholder="Buscar por nombre..."
          v-model="busquedaProyecto"
        />
        <select class="form-select" v-model="proyectoId">
          <option value="">-- Selecciona un proyecto --</option>
          <option
            v-for="p in proyectosFiltrados"
            :key="p.id"
            :value="p.id"
          >
            {{ p.nombre }}
          </option>
        </select>
      </div>

      <!-- Código de trazabilidad -->
      <div class="col-md-6">
        <label class="form-label fw-semibold">Código de trazabilidad</label>
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            v-model="codigoTrazabilidad"
            placeholder="Ej: BIO001"
          />
          <button
            class="btn btn-outline-secondary"
            type="button"
            title="Pegar desde portapapeles"
            @click="pegarDesdePortapapeles"
          >
            <font-awesome-icon :icon="['fas', 'clipboard']" class="me-1" />
            Pegar
          </button>
        </div>
      </div>
    </div>

    <!-- Reportes por Proyecto -->
    <div v-if="reportesPorProyecto.length">
      <h5 class="text-success mb-3 fw-semibold">Reportes vinculados a Proyecto</h5>
      <div class="row gy-3">
        <div
          v-for="(reporte, i) in reportesPorProyecto"
          :key="i"
          class="col-md-6 col-lg-4"
        >
          <button
            class="btn btn-success w-100 text-start"
            @click="descargar(reporte)"
            :disabled="!reporte.habilitado()"
          >
            <font-awesome-icon :icon="['fas', 'file-pdf']" class="me-2" />
            {{ reporte.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reportes por Código -->
    <div v-if="reportesPorCodigo.length" class="mt-5">
      <h5 class="text-warning mb-3 fw-semibold">Reportes por Código de Trazabilidad</h5>
      <div class="row gy-3">
        <div
          v-for="(reporte, i) in reportesPorCodigo"
          :key="i"
          class="col-md-6 col-lg-4"
        >
          <button
            class="btn btn-warning w-100 text-start"
            @click="descargar(reporte)"
            :disabled="!reporte.habilitado()"
          >
            <font-awesome-icon :icon="['fas', 'file-pdf']" class="me-2" />
            {{ reporte.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Otros reportes -->
    <div v-if="reportesOtros.length" class="mt-5">
      <h5 class="text-secondary mb-3 fw-semibold">Otros Reportes</h5>
      <div class="row gy-3">
        <div
          v-for="(reporte, i) in reportesOtros"
          :key="i"
          class="col-md-6 col-lg-4"
        >
          <button
            class="btn btn-outline-secondary w-100 text-start"
            @click="descargar(reporte)"
            :disabled="!reporte.habilitado()"
          >
            <font-awesome-icon :icon="['fas', 'file-pdf']" class="me-2" />
            {{ reporte.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import axios from '@/axios'

const store = useStore()
const proyectos = ref([])
const proyectoId = ref('')
const busquedaProyecto = ref('')
const codigoTrazabilidad = ref('')
const clubActual = computed(() => store.getters.getClubActual)

onMounted(async () => {
  if (!clubActual.value?.id) return
  try {
    const res = await axios.get(`/api/clubes/${clubActual.value.id}/proyectos`)
    proyectos.value = res.data
  } catch (err) {
    console.error('Error al cargar proyectos', err)
  }
})

const proyectosFiltrados = computed(() =>
  proyectos.value.filter(p =>
    p.nombre.toLowerCase().includes(busquedaProyecto.value.toLowerCase())
  )
)

// Reportes agrupados
const reportes = [
  {
    label: 'Resumen del Proyecto',
    endpoint: () => `/api/export-pdf/resumen-proyecto/${proyectoId.value},${clubActual.value.id}`,
    habilitado: () => !!proyectoId.value && !!clubActual.value?.id,
    grupo: 'proyecto'
  },
  {
    label: 'Plan vs Real',
    endpoint: () => `/api/export-pdf/comparacion-plan-vs-real/${proyectoId.value},${clubActual.value.id}`,
    habilitado: () => !!proyectoId.value && !!clubActual.value?.id,
    grupo: 'proyecto'
  },
  {
    label: 'Tareas Reales',
    endpoint: () => `/api/export-pdf/tareas-reales/${clubActual.value.id}/${proyectoId.value}`,
    habilitado: () => !!proyectoId.value && !!clubActual.value?.id,
    grupo: 'proyecto'
  },
  {
    label: 'Tareas Planificadas',
    endpoint: () => `/api/export-pdf/tareas-planificadas/${proyectoId.value},${clubActual.value.id}`,
    habilitado: () => !!proyectoId.value && !!clubActual.value?.id,
    grupo: 'proyecto'
  },
  {
    label: 'Listado de Lotes',
    endpoint: () => `/api/export-pdf/lotes/${clubActual.value.id}/${proyectoId.value}`,
    habilitado: () => !!clubActual.value?.id && !!proyectoId.value,
    grupo: 'proyecto'
  },
  {
    label: 'Listado de Plantas',
    endpoint: () => `/api/export-pdf/plantas/${clubActual.value.id}/${proyectoId.value}`,
    habilitado: () => !!clubActual.value?.id && !!proyectoId.value,
    grupo: 'proyecto'
  },
  {
    label: 'Trazabilidad por Código',
    endpoint: () => `/api/export-pdf/trazabilidad?codigo=${codigoTrazabilidad.value}`,
    habilitado: () => !!codigoTrazabilidad.value,
    grupo: 'codigo'
  },
  {
    label: 'Inventario Actual',
    endpoint: () => `/api/export-pdf/inventario/${clubActual.value.id}`,
    habilitado: () => !!clubActual.value?.id,
    grupo: 'otros'
  }
]

const reportesPorProyecto = computed(() => reportes.filter(r => r.grupo === 'proyecto'))
const reportesPorCodigo = computed(() => reportes.filter(r => r.grupo === 'codigo'))
const reportesOtros = computed(() => reportes.filter(r => r.grupo === 'otros'))

// Pegar desde portapapeles
const pegarDesdePortapapeles = async () => {
  try {
    const texto = await navigator.clipboard.readText()
    codigoTrazabilidad.value = texto.trim()
  } catch {
    alert('No se pudo acceder al portapapeles')
  }
}

// Descargar PDF
const descargar = async (reporte) => {
  try {
    const res = await axios.get(reporte.endpoint(), { responseType: 'blob' })
    const blob = new Blob([res.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${reporte.label.replace(/\s/g, '_')}.pdf`
    link.click()
  } catch (error) {
    console.error('Error al exportar', reporte.label, error)
    alert(`Error al exportar: ${reporte.label}`)
  }
}
</script>
